/**
 * Maneja la carga de archivos multimedia y PDFs para RegistroCompra
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { RegistroCompraService } from './registro-compra.service';
import { NotificationService } from 'src/app/shared/lib/notification/notification.service';
import {
  bytesToBinaryString,
  extractFileNameFromKey,
  getFileNameWithoutExtension,
} from 'src/app/shared/lib/helpers';

/**
 * Estados posibles de un archivo multimedia
 */
export type FileStatus = 'loading' | 'success' | 'error' | 'success load';

export interface RcMultimediaFile {
  src?: string;        // Base64 data o binary string (undefined si está loading)
  name: string;
  estado: FileStatus;  // Estado del archivo
  key?: string;       // S3 Key para delete (formato: "rcId;fileName")
  nameDownload?: string;
  error?: string;     // Mensaje de error si estado es 'error'
}

@Injectable({ providedIn: 'root' })
export class RegistroCompraUploadService {
  private readonly s3ServiceRoute = `${environment.financialsApi}/api/v1`;

  private http = inject(HttpClient);
  private registroCompraService = inject(RegistroCompraService);
  private notificationService = inject(NotificationService);

  // MULTIMEDIA UPLOAD

  /**
   * Carga archivos multimedia para un Registro de Compra
   * Solo sube archivos nuevos (que tienen 'src' y NO tienen 'key')
   * @param rcId ID del Registro de Compra
   * @param multimedia Array de archivos (nuevos + existentes)
   * @returns Promise que resuelve cuando se suben los archivos nuevos
   */
  uploadMultimedia(rcId: string, multimedia: RcMultimediaFile[]): Promise<any[]> {
    if (!multimedia || multimedia.length === 0) {
      return Promise.resolve([]);
    }

    // Filtrar SOLO archivos nuevos:
    // - Tienen 'src' definido (contenido del archivo)
    // - NO tienen 'key' (no están en S3 aún)
    const newFiles = multimedia.filter((m) => m.src !== undefined && !m.key);

    if (newFiles.length === 0) {
      return Promise.resolve([]);
    }

    const uploadPromises = newFiles.map((file) =>
      this.uploadSingleFile(rcId, file)
    );

    return Promise.all(uploadPromises)
      .then((results) => {
        return results;
      })
      .catch((error) => {
        console.error('Error al subir archivos:', error);
        this.notificationService.notifyError('Error al subir archivos adjuntos');
        throw error;
      });
  }

  /**
   * Carga un archivo individual
   * @param rcId ID del Registro de Compra
   * @param file Archivo a subir
   * @returns Promise con la respuesta del servidor
   */
  private uploadSingleFile(rcId: string, file: RcMultimediaFile): Promise<any> {
    return this.http
      .post(`${this.s3ServiceRoute}/addFileRC`, {
        data: file.src,      // Base64 string
        rcId,
        fileName: file.name,
      })
      .toPromise()
      .catch((error) => {
        console.error(`Error al subir archivo ${file.name}:`, error);
        throw error;
      });
  }

  // DOCUMENT

  /**
   * Marca que el Registro de Compra tiene documentos adjuntos
   * @param rcId ID del Registro de Compra
   * @deprecated Usar la lógica de cálculo en el componente en su lugar
   */
  // private markRCHasDocument(rcId: string): void {
  //   this.registroCompraService
  //     .updateHasDocument(rcId, true)
  //     .subscribe({
  //       next: (resp) => {
  //         console.log('Registro de Compra marcado con documentos:', resp);
  //       },
  //       error: (error) => {
  //         console.error('Error al marcar documento:', error);
  //         this.notificationService.notifyWarning(
  //           'No se pudo actualizar el estado de documentos'
  //         );
  //       },
  //     });
  // }

  /**
   * Actualiza el estado de documento de un Registro de Compra
   * @param rcId ID del Registro de Compra
   * @param hasDocument Indica si tiene documentos
   * @returns Observable con la respuesta
   */
  updateHasDocument(rcId: string, hasDocument: boolean): Observable<void> {
    return this.registroCompraService.updateHasDocument(rcId, hasDocument);
  }

  // MULTIMEDIA RETRIEVAL

  /**
   * Obtiene la lista de archivos multimedia de un Registro de Compra desde S3
   * @param rcId ID del Registro de Compra
   * @returns Observable con array de archivos
   */
  getMultimediaFiles(rcId: string): Observable<RcMultimediaFile[]> {
    return this.http
      .get<any[]>(`${this.s3ServiceRoute}/getFilesRC/${rcId}`)
      .pipe(
        switchMap((files: any[]) => {
          if (!files || files.length === 0) {
            return of([]);
          }

          // Por cada Key, obtener el contenido del archivo
          const requests = files.map((el: any) =>
            this.http
              .get<any>(`${this.s3ServiceRoute}/getFileRC/${el.Key}`)
              .pipe(
                map((res: any) => {
                  // Convertir bytes a binary string usando helper
                  const binary = bytesToBinaryString(new Uint8Array(res.data));

                  // Extraer nombre del archivo desde Key usando helper
                  const fileName = extractFileNameFromKey(el.Key);

                  return {
                    src: binary,
                    key: el.Key,
                    name: fileName,
                    nameDownload: getFileNameWithoutExtension(fileName),
                    estado: 'success load' as FileStatus,
                  };
                }),
                catchError((error) => {
                  console.error(`Error cargando archivo ${el.Key}:`, error);
                  const fileName = extractFileNameFromKey(el.Key);
                  return of({
                    key: el.Key,
                    name: fileName,
                    nameDownload: getFileNameWithoutExtension(fileName),
                    estado: 'error' as FileStatus,
                    error: 'Error al cargar archivo',
                  });
                })
              )
          );

          return forkJoin(requests).pipe(
            map((results) => results as RcMultimediaFile[])
          );
        }),
        catchError((error) => {
          console.error('Error obteniendo archivos multimedia:', error);
          this.notificationService.notifyError(
            'Error al cargar los archivos adjuntos'
          );
          return of([]);
        })
      );
  }

  /**
   * Obtiene el contenido de un archivo específico desde S3
   * @param key Key del archivo en S3 (formato: "rcId;fileName")
   * @returns Observable con el contenido del archivo
   */
  getFileContent(key: string): Observable<RcMultimediaFile | null> {
    return this.http.get<any>(`${this.s3ServiceRoute}/getFileRC/${key}`).pipe(
      map((res: any) => {
        // Convertir bytes a binary string usando helper
        const binary = bytesToBinaryString(new Uint8Array(res.data));

        // Extraer nombre del archivo desde Key usando helper
        const fileName = extractFileNameFromKey(key);

        return {
          src: binary,
          key: key,
          name: fileName,
          nameDownload: getFileNameWithoutExtension(fileName),
          estado: 'success load' as FileStatus,
        };
      }),
      catchError((error) => {
        console.error(`Error obteniendo archivo ${key}:`, error);
        this.notificationService.notifyError('Error al obtener el archivo');
        const fileName = extractFileNameFromKey(key);
        return of({
          key: key,
          name: fileName,
          nameDownload: getFileNameWithoutExtension(fileName),
          estado: 'error' as FileStatus,
          error: 'Error al obtener el archivo',
        });
      })
    );
  }

  /**
   * Elimina un archivo multimedia de S3
   * @param key Key del archivo en S3 (formato: "rcId;fileName")
   * @returns Observable que completa cuando el archivo se elimina
   */
  deleteMultimediaFile(key: string): Observable<void> {
    return this.http
      .delete<void>(`${this.s3ServiceRoute}/deleteFileRC/${key}`)
      .pipe(
        catchError((error) => {
          console.error(`Error al eliminar archivo ${key}:`, error);
          this.notificationService.notifyError('Error al eliminar el archivo');
          throw error;
        })
      );
  }

  /**
   * Elimina un archivo y actualiza el estado hasDocument si es necesario
   * @param rcId ID del Registro de Compra
   * @param key Key del archivo a eliminar
   * @param isLastFile Indica si es el último archivo del RC
   * @returns Observable que completa cuando se elimina el archivo y actualiza el RC
   */
  deleteMultimediaFileWithUpdate(
    rcId: string,
    key: string,
    isLastFile: boolean
  ): Observable<any> {
    const requests: Observable<any>[] = [this.deleteMultimediaFile(key)];

    // Si es el último archivo, actualizar hasDocument = false
    if (isLastFile) {
      requests.push(this.updateHasDocument(rcId, false));
    }

    return forkJoin(requests).pipe(
      catchError((error) => {
        console.error('Error al eliminar archivo:', error);
        this.notificationService.notifyError('Error al eliminar archivo');
        throw error;
      })
    );
  }
}
